import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router"
import { AlertTriangle, CheckCircle2, UploadIcon } from "lucide-react"
import { useState, useMemo } from "react"
import type { ProjectVersion } from "@/types"
import { EditorHeader } from "@/components/editor-header"
import { Card, CardContent } from "@/components/ui/card"
import { projectsTable } from "@/db/schema"
import { DuplicatePdfWarningDialog } from "@/features/project-editor/components/DuplicatePdfWarningDialog"
import { getWorkflowSteps } from "@/features/project-editor/core/workflowSteps"
import {
  uploadProjectFile,
  checkForDuplicatePdf,
  logDuplicateUploadDecision,
  type DuplicatePdfCheckResult,
} from "@/features/project-editor/server/projectServer"
import { usePermissions } from "@/hooks/usePermissions"
import { projectQuery } from "../edit.$projectId/route"

/**
 * Parsed filename components extracted from a blueprint PDF filename.
 */
export interface ParsedFilename {
  /** Store number (e.g., "1001", "A001", "6002") */
  storeNumber: string | null
  /** Store name/location (e.g., "Enkhuizen (Koperwiekplein)") */
  storeName: string | null
  /** Document type code (e.g., "UO-011", "UO-012", "DOE-011") */
  documentType: string | null
  /** Document date in ISO format YYYY-MM-DD */
  documentDate: string | null
  /** Version letter (e.g., "A", "B", "H") */
  version: string | null
  /** Original filename */
  raw: string
}

// Store number pattern: leading alphanumeric (1001, 3001, 6002, A001, B001, AB001)
// Matches 0-2 letters followed by 3-4 digits
const STORE_NUMBER_PATTERN = /^([A-Za-z]{0,2}\d{3,4})/

// Document type pattern: UO-011, UO-012, DOE-011, etc.
const DOCUMENT_TYPE_PATTERN = /(UO|DOE|CL)-(\d{3})/i

// Date patterns:
// 1. ISO format: YYYY-MM-DD (2017-10-03)
const ISO_DATE_PATTERN = /(\d{4})-(\d{2})-(\d{2})/

// 2. European format: DD-MM-YYYY (02-12-2025)
const EUROPEAN_DATE_WITH_SEPARATORS_PATTERN = /(\d{2})-(\d{2})-(\d{4})/

// 3. European format without separators: DDMMYYYY (02122025)
const EUROPEAN_DATE_NO_SEPARATORS_PATTERN = /(?<!\d)(\d{8})(?!\d)/g

// Version pattern: trailing single uppercase letter before .pdf
const VERSION_PATTERN = /-([A-Za-z])(?:\.pdf)?$/i

function isSeparator(c: string): boolean {
  return c === " " || c === "_" || c === "-"
}

function isValidDate(year: string, month: string, day: string): boolean {
  const y = parseInt(year, 10)
  const m = parseInt(month, 10)
  const d = parseInt(day, 10)

  if (isNaN(y) || isNaN(m) || isNaN(d)) return false
  if (y < 1900 || y > 2100) return false
  if (m < 1 || m > 12) return false
  if (d < 1 || d > 31) return false

  // More precise validation using Date
  try {
    const date = new Date(y, m - 1, d)
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
  } catch {
    return false
  }
}

function extractStoreNumber(baseName: string): string | null {
  const match = STORE_NUMBER_PATTERN.exec(baseName)
  return match ? match[1] : null
}

function extractDocumentType(baseName: string): string | null {
  const match = DOCUMENT_TYPE_PATTERN.exec(baseName)
  if (match) {
    // Normalize to uppercase with hyphen: UO-011
    return `${match[1].toUpperCase()}-${match[2]}`
  }
  return null
}

function extractDate(baseName: string): string | null {
  // Try ISO format first (YYYY-MM-DD)
  const isoMatch = ISO_DATE_PATTERN.exec(baseName)
  if (isoMatch) {
    const [, year, month, day] = isoMatch
    if (isValidDate(year, month, day)) {
      return `${year}-${month}-${day}`
    }
  }

  // Try European format with separators (DD-MM-YYYY)
  const euroSepMatch = EUROPEAN_DATE_WITH_SEPARATORS_PATTERN.exec(baseName)
  if (euroSepMatch) {
    const [, day, month, year] = euroSepMatch
    if (isValidDate(year, month, day)) {
      return `${year}-${month}-${day}`
    }
  }

  // Try European format without separators (DDMMYYYY)
  // Reset regex lastIndex for global pattern
  EUROPEAN_DATE_NO_SEPARATORS_PATTERN.lastIndex = 0
  let euroNoSepMatch
  while ((euroNoSepMatch = EUROPEAN_DATE_NO_SEPARATORS_PATTERN.exec(baseName)) !== null) {
    const digits = euroNoSepMatch[1]
    const day = digits.slice(0, 2)
    const month = digits.slice(2, 4)
    const year = digits.slice(4, 8)

    if (isValidDate(year, month, day)) {
      return `${year}-${month}-${day}`
    }
  }

  return null
}

function extractVersion(baseName: string): string | null {
  const match = VERSION_PATTERN.exec(baseName)
  return match ? match[1].toUpperCase() : null
}

function extractStoreName(
  baseName: string,
  storeNumber: string | null,
  documentType: string | null,
): string | null {
  if (!storeNumber) {
    return null
  }

  // Find the position after the store number
  const storeNumberIndex = baseName.indexOf(storeNumber)
  let startIndex = storeNumberIndex + storeNumber.length

  // Determine the separator used after the store number
  let separatorAfterStoreNumber = ""
  if (startIndex < baseName.length) {
    separatorAfterStoreNumber = baseName[startIndex]
  }

  // Skip any leading separators (space, underscore, hyphen)
  while (startIndex < baseName.length && isSeparator(baseName[startIndex])) {
    startIndex++
  }

  if (startIndex >= baseName.length) {
    return null
  }

  // Find where the store name ends
  // If we have a document type, it ends before that
  let endIndex: number

  if (documentType) {
    let docTypeIndex = baseName.toLowerCase().indexOf(documentType.toLowerCase(), startIndex)
    if (docTypeIndex === -1) {
      // Try without hyphen (some files might have UO011 instead of UO-011)
      const docTypeNoHyphen = documentType.replace("-", "")
      docTypeIndex = baseName.toLowerCase().indexOf(docTypeNoHyphen.toLowerCase(), startIndex)
    }

    if (docTypeIndex > startIndex) {
      endIndex = docTypeIndex
    } else {
      endIndex = baseName.length
    }
  } else {
    // Look for date patterns or end of string
    const isoMatch = ISO_DATE_PATTERN.exec(baseName.slice(startIndex))
    if (isoMatch?.index !== undefined) {
      endIndex = startIndex + isoMatch.index
    } else {
      endIndex = baseName.length
    }
  }

  // Trim separators from the end
  while (endIndex > startIndex && isSeparator(baseName[endIndex - 1])) {
    endIndex--
  }

  if (endIndex <= startIndex) {
    return null
  }

  let storeName = baseName.slice(startIndex, endIndex).trim()

  // Clean up the store name
  // For dash-separated filenames, take only the first segment (city name)
  if (separatorAfterStoreNumber === "-") {
    if (!storeName.includes("(") && storeName.includes("-")) {
      const dashIndex = storeName.indexOf("-")
      if (dashIndex > 0) {
        storeName = storeName.slice(0, dashIndex)
      }
    }
  }

  // Remove any document type suffixes that might have slipped in
  storeName = storeName.replace(DOCUMENT_TYPE_PATTERN, "")

  // Remove date patterns
  storeName = storeName.replace(ISO_DATE_PATTERN, "")
  storeName = storeName.replace(EUROPEAN_DATE_WITH_SEPARATORS_PATTERN, "")

  // Trim trailing separators and whitespace
  storeName = storeName.replace(/[\s_-]+$/, "")

  return storeName || null
}

/**
 * Parses a blueprint PDF filename to extract metadata.
 * Client-side equivalent of backend FilenameParser.cs
 */
export function parseFilename(filename: string): ParsedFilename {
  if (!filename?.trim()) {
    return {
      storeNumber: null,
      storeName: null,
      documentType: null,
      documentDate: null,
      version: null,
      raw: filename ?? "",
    }
  }

  const raw = filename

  // Remove .pdf extension if present
  let baseName = filename
  if (baseName.toLowerCase().endsWith(".pdf")) {
    baseName = baseName.slice(0, -4)
  }

  // Extract components
  const storeNumber = extractStoreNumber(baseName)
  const documentType = extractDocumentType(baseName)
  const documentDate = extractDate(baseName)
  const version = extractVersion(baseName)
  const storeName = extractStoreName(baseName, storeNumber, documentType)

  return {
    storeNumber,
    storeName,
    documentType,
    documentDate,
    version,
    raw,
  }
}

/**
 * Checks if the filename matches the expected blueprint PDF pattern.
 * Returns true if at least store number and document type are found.
 */
export function isValidFilenamePattern(parsed: ParsedFilename): boolean {
  return Boolean(parsed.storeNumber && parsed.documentType)
}

/**
 * Formats a document type code for display.
 * UO-011 -> "Floor Plan (UO-011)"
 * UO-012 -> "Layout (UO-012)"
 * DOE-011 -> "DOE Document (DOE-011)"
 */
function formatDocumentType(docType: string): string {
  if (docType.startsWith("UO-011")) return t`Floor Plan (UO-011)`
  if (docType.startsWith("UO-012")) return t`Layout (UO-012)`
  if (docType.startsWith("DOE-")) return t`DOE Document (${docType})`
  if (docType.startsWith("CL-")) return t`Checklist (${docType})`
  return docType
}

/**
 * Formats a date string for display using locale-appropriate format.
 */
function formatDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  } catch {
    return dateStr
  }
}

interface ParsedFilenamePreviewProps {
  parsed: ParsedFilename
  isValid: boolean
}

function ParsedFilenamePreview({ parsed, isValid }: ParsedFilenamePreviewProps) {
  return (
    <Card
      className={`w-full ${isValid ? "border-green-200 dark:border-green-800" : "border-yellow-300 dark:border-yellow-700"}`}
    >
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          {isValid ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          ) : (
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600 dark:text-yellow-400" />
          )}
          <div className="flex-1 space-y-3">
            {isValid ? (
              <>
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  <Trans>File information detected from filename</Trans>
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                  {parsed.storeNumber && (
                    <>
                      <dt className="text-muted-foreground">
                        <Trans>Store Number</Trans>
                      </dt>
                      <dd className="font-medium">{parsed.storeNumber}</dd>
                    </>
                  )}
                  {parsed.storeName && (
                    <>
                      <dt className="text-muted-foreground">
                        <Trans>Store Name</Trans>
                      </dt>
                      <dd className="font-medium">{parsed.storeName}</dd>
                    </>
                  )}
                  {parsed.documentType && (
                    <>
                      <dt className="text-muted-foreground">
                        <Trans>Document Type</Trans>
                      </dt>
                      <dd className="font-medium">{formatDocumentType(parsed.documentType)}</dd>
                    </>
                  )}
                  {parsed.documentDate && (
                    <>
                      <dt className="text-muted-foreground">
                        <Trans>Document Date</Trans>
                      </dt>
                      <dd className="font-medium">{formatDate(parsed.documentDate)}</dd>
                    </>
                  )}
                  {parsed.version && (
                    <>
                      <dt className="text-muted-foreground">
                        <Trans>Version</Trans>
                      </dt>
                      <dd className="font-medium">{parsed.version}</dd>
                    </>
                  )}
                </dl>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                  <Trans>Filename does not match expected format</Trans>
                </p>
                <p className="text-muted-foreground text-sm">
                  <Trans>
                    Expected format:{" "}
                    <code className="rounded bg-neutral-100 px-1 dark:bg-neutral-800">
                      1001_Store Name_UO-011_2025-01-01.pdf
                    </code>
                  </Trans>
                </p>
                <p className="text-muted-foreground text-sm">
                  <Trans>
                    You can still upload this file, but metadata will need to be entered manually.
                  </Trans>
                </p>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Step 1: Upload PDF
export const Route = createFileRoute("/(authenticated)/projects/new/$projectId/upload")({
  component: ProjectStep1Upload,
  // No loader needed here if project data comes from the layout context/loader
})

function ProjectStep1Upload() {
  // Get project data from the PARENT layout route using parentRoute
  const { data } = useSuspenseQuery(projectQuery(Route.useParams()))
  const { project } = data as { project: typeof projectsTable.$inferSelect; configVersion: unknown }
  const navigate = useNavigate()
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<DuplicatePdfCheckResult | null>(
    null,
  )
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false)
  const [checkingDuplicate, setCheckingDuplicate] = useState(false)

  // Parse filename when file is selected
  const parsedFilename = useMemo(() => {
    if (!selectedFile) return null
    return parseFilename(selectedFile.name)
  }, [selectedFile])

  const isFilenameValid = parsedFilename ? isValidFilenamePattern(parsedFilename) : true

  // Access control: Only users with project:update permission can upload
  const { hasPermission } = usePermissions()
  const canUpload = hasPermission("project", "update").allowed

  // Mutation for logging duplicate decisions
  const logDecisionMutation = useMutation({
    mutationFn: logDuplicateUploadDecision,
  })

  if (!canUpload) {
    return <Navigate to="/projects" />
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setSelectedFile(file)
    setUploadError(null)
    setDuplicateCheckResult(null)
    // Check for duplicates if project has a store linked
    void handleDuplicateCheckAndUpload(file)
  }

  const handleDuplicateCheckAndUpload = async (file: File) => {
    // If project has a store linked, check for duplicates first
    if (project.storeId) {
      setCheckingDuplicate(true)
      try {
        const result = await checkForDuplicatePdf({
          data: {
            storeId: project.storeId,
            fileName: file.name,
          },
        })

        if (result.isDuplicate && result.existingProject) {
          // Show warning dialog
          setDuplicateCheckResult(result)
          setShowDuplicateWarning(true)
          setCheckingDuplicate(false)
          return
        }
      } catch (error) {
        // If duplicate check fails, proceed with upload anyway
        console.error("Duplicate check failed:", error)
      } finally {
        setCheckingDuplicate(false)
      }
    }

    // No duplicate found or no store linked, proceed with upload
    await handleFileUpload(file)
  }

  const handleUseExisting = () => {
    if (!duplicateCheckResult?.existingProject) return

    // Log the decision
    if (selectedFile) {
      void logDecisionMutation.mutateAsync({
        data: {
          projectId: project.id,
          fileName: selectedFile.name,
          existingProjectId: duplicateCheckResult.existingProject.id,
          existingProjectName: duplicateCheckResult.existingProject.name,
          decision: "use_existing",
        },
      })
    }

    // Navigate to the existing project
    setShowDuplicateWarning(false)
    void navigate({
      to: `/projects/edit/${duplicateCheckResult.existingProject.id}/process`,
    })
  }

  const handleUploadAnyway = async () => {
    if (!selectedFile || !duplicateCheckResult?.existingProject) return

    // Log the decision
    await logDecisionMutation.mutateAsync({
      data: {
        projectId: project.id,
        fileName: selectedFile.name,
        existingProjectId: duplicateCheckResult.existingProject.id,
        existingProjectName: duplicateCheckResult.existingProject.name,
        decision: "upload_anyway",
      },
    })

    // Close dialog and proceed with upload
    setShowDuplicateWarning(false)
    await handleFileUpload(selectedFile)
  }

  const handleFileUpload = async (file: File) => {
    setUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append("projectId", project.id)
      formData.append("fileName", file.name)
      formData.append("file", file)

      const result = await uploadProjectFile({ data: formData })

      if (result.success) {
        // Navigate to the next step on successful upload
        await navigate({
          to: "/projects/new/$projectId/crop",
          params: { projectId: project.id }, // Keep projectId in params for type safety
          search: {}, // Add empty search object to satisfy type
        })
      } else {
        // Handle potential non-error failure cases if the server function returns them
        setUploadError("Upload failed, please try again.")
      }
    } catch (error: unknown) {
      // console.error("Upload failed:", error)
      // Display specific error from the server function if available
      const message = error instanceof Error ? error.message : "Unknown error during upload."
      // Check if it's a Response object to get status/body
      if (error instanceof Response) {
        const responseText = await error.text()
        setUploadError(`Upload failed (${error.status}): ${responseText || message}`)
      } else {
        setUploadError(`Upload failed: ${message}`)
      }
    } finally {
      setUploading(false)
    }
  }

  // Check if PDF has been uploaded using the pdfFileUploaded flag in metadata
  const metadata = project.metadata as ProjectVersion | undefined
  const hasPdfUploaded = metadata?.pdfFileUploaded === true

  return (
    <div className="flex h-full flex-col">
      {/* Editor Header */}
      <EditorHeader
        contextLabel={t`Upload PDF`}
        projectName={project.name}
        steps={getWorkflowSteps({
          projectId: project.id,
          hasStore: Boolean(project.storeId),
          hasPdf: hasPdfUploaded,
          hasCropData: false,
          hasRows: false,
        })}
        currentStepId="upload"
        projectId={project.id}
      />

      {/* Upload Content */}
      <div className="flex flex-grow items-center justify-center p-8">
        <div className="flex w-full max-w-md flex-col items-center">
          <UploadIcon className="text-muted-foreground mb-6 h-16 w-16" />
          <h2 className="mb-2 text-xl font-semibold">{t`Upload PDF`}</h2>
          <p className="text-muted-foreground mb-6 text-center text-sm">
            {t`Upload the project's PDF file to begin. Only .pdf files are accepted.`}
          </p>
          {uploadError && (
            <div className="mb-4 w-full rounded border border-red-400 bg-red-100 p-3 text-center text-sm text-red-700 dark:border-red-500 dark:bg-red-900/50 dark:text-red-200">
              {uploadError}
            </div>
          )}
          <label
            className={`mb-4 flex w-full cursor-pointer flex-col items-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 px-6 py-8 transition hover:border-blue-400 hover:bg-blue-50 dark:border-neutral-600 dark:bg-neutral-800 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 ${uploading || checkingDuplicate ? "cursor-not-allowed opacity-75" : ""}`}
          >
            <UploadIcon className="text-muted-foreground mb-2 h-8 w-8" />
            <span className="text-sm font-medium">
              {checkingDuplicate
                ? t`Checking for duplicates...`
                : uploading
                  ? t`Uploading...`
                  : t`Click to select a PDF file`}
            </span>
            <span className="text-muted-foreground mt-1 text-xs">{t`or drag and drop`}</span>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              disabled={uploading || checkingDuplicate}
              className="hidden"
            />
          </label>
          {/* Show parsed filename info after file selection */}
          {parsedFilename && (
            <div className="mb-4 w-full">
              <ParsedFilenamePreview parsed={parsedFilename} isValid={isFilenameValid} />
            </div>
          )}
          {(uploading || checkingDuplicate) && (
            <div className="mt-2 flex items-center gap-2">
              <svg
                className="h-5 w-5 animate-spin text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              <span className="text-sm text-blue-600">
                {checkingDuplicate ? t`Checking for duplicates...` : t`Processing...`}
              </span>
            </div>
          )}
          <span className="text-muted-foreground mt-4 text-xs">{t`Max file size: 500MB`}</span>
        </div>
      </div>

      {/* Duplicate PDF Warning Dialog */}
      <DuplicatePdfWarningDialog
        open={showDuplicateWarning}
        onOpenChange={setShowDuplicateWarning}
        duplicateInfo={duplicateCheckResult}
        onUseExisting={handleUseExisting}
        onUploadAnyway={() => void handleUploadAnyway()}
        isLoading={uploading || logDecisionMutation.isPending}
      />
    </div>
  )
}
